/*
 * Copyright (c) by INFODATA-GROUP. All rights reserved.
 * No part of this publication may be reproduced, distributed,
 * or transmitted in any form or by any means, including photocopying, recording,
 * or other electronic or mechanical methods, without the prior written permission of the publisher,
 * except in the case of brief quotations embodied in critical
 * reviews and certain other noncommercial uses permitted by copyright law.
 *  author : edg
 *  date : 08/03/2024
 */

import { inject, Injectable } from '@angular/core';
import { Note } from '../model/notification/note';
import { BehaviorSubject } from 'rxjs';
import { NotificationEvent } from '../model/notification/notification-event.enum';
import { WebsocketService } from './websocket.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NoteBuilder } from '../builder/note.builder';
import { WebsocketPayloadNoteType } from '../types/websocket/websocket-event-note.type';
import { Style } from '../enum/style.enum';

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private websocketService = inject(WebsocketService);

  private notifications: Note[] = [];
  private notificationObserver = new BehaviorSubject<Note[]>([]);
  public currentNotifying$ = this.notificationObserver.asObservable();

  constructor() {
    this.websocketService.events$.pipe(takeUntilDestroyed()).subscribe({
      next: (event) => {
        if (event.type !== 'notification') {
          return;
        }
        this.createNoteFromWebsocket(event.payload);
      },
    });
  }

  public addNotification(notification: Note) {
    notification.id += this.notifications.length;
    this.notifications.push(notification);
    notification.notifyAction$.subscribe({
      next: (event) => {
        if (event === NotificationEvent.close) {
          this.removeNotification(
            this.notifications.findIndex((n) => n.id === notification.id),
          );
        }
      },
    });
    this.notificationObserver.next(this.notifications);
  }

  public removeNotification(value: number | Note) {
    if (value instanceof Note) {
      value = this.notifications.findIndex((n) => n.id === (value as Note).id);
    }
    this.notifications.splice(value, 1);
    this.notificationObserver.next(this.notifications);
  }

  public removeAllNotifications() {
    this.notifications = [];
    this.notificationObserver.next(this.notifications);
  }

  private createNoteFromWebsocket(payload: WebsocketPayloadNoteType) {
    const note = new NoteBuilder(
      payload.title,
      payload.icon,
      payload.content,
    ).setInfoNote();
    if (payload.style && payload.style === Style.danger) {
      note.setAlertNote();
    }
    if (payload.info) {
      note.setInfo(payload.info);
    }
    if (payload.time) {
      note.setTime(payload.time);
    }
    if (payload.style) {
      note.setStyle(payload.style);
    }
    this.addNotification(note.build());
  }
}
